import { safeDisplayUrl } from '../safeDisplayUrl';
import { useEffect, useRef, type CSSProperties } from 'react';
import type { Terminal } from '@xterm/xterm';

export interface RuntimeTerminalOutputFrame { sequence: number; data: string }
export type RuntimeTerminalOutputErrorCode = 'invalid-frame' | 'duplicate-sequence' | 'sequence-gap' | 'sequence-conflict' | 'history-expired' | 'output-budget-exceeded' | 'load-failed';
export interface RuntimeTerminalViewProps {
  sessionKey: string;
  output: readonly RuntimeTerminalOutputFrame[];
  statusLabel: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  onInput?: (event: { sessionKey: string; data: string }) => void;
  onResize?: (event: { sessionKey: string; columns: number; rows: number }) => void;
  onOutputError?: (event: { sessionKey: string; code: RuntimeTerminalOutputErrorCode; expectedSequence: number; receivedSequence?: number }) => void;
}
export const RUNTIME_TERMINAL_MAX_RETAINED_FRAMES = 2048;
export const RUNTIME_TERMINAL_MAX_RETAINED_BYTES = 2 * 1024 * 1024;
interface RetainedFrame extends RuntimeTerminalOutputFrame { bytes: number }
export interface RuntimeTerminalOutputHistory { highWater: number; bytes: number; frames: readonly RetainedFrame[] }
export class RuntimeTerminalOutputError extends Error {
  constructor(readonly code: RuntimeTerminalOutputErrorCode, readonly expectedSequence: number, readonly receivedSequence?: number) { super(code); this.name = 'RuntimeTerminalOutputError'; }
}
/** Validate the entire batch before any write. Old replay beyond the bounded window fails closed. */
export function prepareRuntimeTerminalOutput(history: RuntimeTerminalOutputHistory, output: readonly RuntimeTerminalOutputFrame[]): { history: RuntimeTerminalOutputHistory; append: readonly RuntimeTerminalOutputFrame[] } {
  const fail = (code: RuntimeTerminalOutputErrorCode, received?: number): never => { throw new RuntimeTerminalOutputError(code, history.highWater + 1, received); };
  if (!Array.isArray(output)) return fail('invalid-frame');
  if (output.length > RUNTIME_TERMINAL_MAX_RETAINED_FRAMES) return fail('output-budget-exceeded');
  const known = new Map(history.frames.map(frame => [frame.sequence, frame]));
  const append: RetainedFrame[] = [];
  let highWater = history.highWater, previous: number | undefined, batchBytes = 0;
  for (const frame of output) {
    if (!frame || !Number.isSafeInteger(frame.sequence) || frame.sequence < 1 || typeof frame.data !== 'string') return fail('invalid-frame');
    if (previous !== undefined && frame.sequence === previous) return fail('duplicate-sequence', frame.sequence);
    if (previous !== undefined && frame.sequence !== previous + 1) return fail('sequence-gap', frame.sequence);
    previous = frame.sequence;
    if (frame.data.length > RUNTIME_TERMINAL_MAX_RETAINED_BYTES) return fail('output-budget-exceeded', frame.sequence);
    const bytes = new TextEncoder().encode(frame.data).byteLength;
    batchBytes += bytes;
    if (batchBytes > RUNTIME_TERMINAL_MAX_RETAINED_BYTES) return fail('output-budget-exceeded', frame.sequence);
    if (frame.sequence <= highWater) {
      const seen = known.get(frame.sequence);
      if (!seen) return fail('history-expired', frame.sequence);
      if (seen.data !== frame.data) return fail('sequence-conflict', frame.sequence);
    } else {
      if (frame.sequence !== highWater + 1) return fail('sequence-gap', frame.sequence);
      highWater = frame.sequence;
      append.push({ sequence: frame.sequence, data: frame.data, bytes });
    }
  }
  const frames = [...history.frames, ...append];
  let bytes = history.bytes + append.reduce((sum, frame) => sum + frame.bytes, 0), remove = 0;
  while (frames.length - remove > RUNTIME_TERMINAL_MAX_RETAINED_FRAMES || bytes > RUNTIME_TERMINAL_MAX_RETAINED_BYTES) bytes -= frames[remove++]!.bytes;
  return { history: { highWater, bytes, frames: frames.slice(remove) }, append: append.map(({ sequence, data }) => ({ sequence, data })) };
}

/** Rendering only. Authentication, network transport and authoritative retained frames belong to the host. */
export function RuntimeTerminalView(props: RuntimeTerminalViewProps) {
  const element = useRef<HTMLDivElement>(null);
  const latest = useRef(props); latest.current = props;
  const running = useRef<{ sessionKey: string; terminal: Terminal; write(output: readonly RuntimeTerminalOutputFrame[]): void }>();
  useEffect(() => {
    const sessionKey = props.sessionKey;
    const container = element.current;
    if (!container || !sessionKey) return;
    let active = true, terminal: Terminal | undefined, observer: ResizeObserver | undefined;
    const subscriptions: { dispose(): void }[] = [];
    let cleaned = false;
    const cleanup = () => { if (cleaned) return; cleaned = true; observer?.disconnect(); subscriptions.forEach(subscription => subscription.dispose()); terminal?.dispose(); };
    let history: RuntimeTerminalOutputHistory = { highWater: 0, bytes: 0, frames: [] };
    const current = () => active && latest.current.sessionKey === sessionKey;
    const report = (error: unknown) => {
      if (!current()) return;
      const fault = error instanceof RuntimeTerminalOutputError ? error : new RuntimeTerminalOutputError('load-failed', history.highWater + 1);
      latest.current.onOutputError?.({ sessionKey, code: fault.code, expectedSequence: fault.expectedSequence, ...(fault.receivedSequence === undefined ? {} : { receivedSequence: fault.receivedSequence }) });
    };
    void Promise.all([import('@xterm/xterm'), import('@xterm/addon-fit'), import('@xterm/addon-web-links')]).then(([{ Terminal }, { FitAddon }, { WebLinksAddon }]) => {
      if (!current()) return;
      const openLink = (_event: MouseEvent, uri: string) => { const safe = safeDisplayUrl(uri, 'link'); if (current() && safe && /^https?:\/\//i.test(safe)) window.open(safe, '_blank', 'noopener,noreferrer'); };
      const term = new Terminal({ linkHandler: { activate: openLink }, cursorBlink: true, disableStdin: latest.current.disabled === true, fontSize: 13, fontFamily: '"JetBrains Mono", Menlo, Monaco, "Courier New", monospace' });
      terminal = term;
      const fit = new FitAddon(); term.loadAddon(fit); term.loadAddon(new WebLinksAddon(openLink)); term.open(container);
      const resize = () => {
        if (!current()) return;
        fit.fit();
        const dimensions = fit.proposeDimensions();
        if (dimensions && Number.isInteger(dimensions.cols) && Number.isInteger(dimensions.rows) && dimensions.cols > 0 && dimensions.rows > 0) latest.current.onResize?.({ sessionKey, columns: dimensions.cols, rows: dimensions.rows });
      };
      subscriptions.push(term.onData(data => { if (current() && !latest.current.disabled) latest.current.onInput?.({ sessionKey, data }); }));
      const write = (output: readonly RuntimeTerminalOutputFrame[]) => {
        if (!current()) return;
        try {
          const prepared = prepareRuntimeTerminalOutput(history, output);
          if (prepared.append.length) term.write(prepared.append.map(frame => frame.data).join(''));
          history = prepared.history;
        } catch (error) { report(error); }
      };
      running.current = { sessionKey, terminal: term, write };
      resize();
      if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(resize); observer.observe(container); }
      write(latest.current.output);
    }).catch(error => {
      cleanup();
      if (terminal && running.current?.terminal === terminal) running.current = undefined;
      report(error);
    });
    return () => {
      active = false;
      cleanup();
      if (terminal && running.current?.terminal === terminal) running.current = undefined;
    };
  }, [props.sessionKey]);
  useEffect(() => {
    const state = running.current;
    if (state?.sessionKey === props.sessionKey) { state.terminal.options.disableStdin = props.disabled === true; state.write(props.output); }
  }, [props.sessionKey, props.output, props.disabled]);
  return <div className={props.className} style={props.style}>
    <div role="status">{props.statusLabel}</div>
    <div ref={element} className="h-full min-h-48 w-full rounded-2xl" />
  </div>;
}
